import * as djangioActionCreators from "shared/djangio/action-creators"

import actionTypes, {
    deleteList as deleteListAC,
    editNewList,
    getIssueManagementLists,
    submitNewList,
} from "projects-new/issue-management/action-types"

import { loadList, loadLists } from 'projects-new/new-list/action-creators'
import swalContents from 'projects-new/issue-management/components/helpTexts'

/**
 * loads up all lists for this issue, load them into the newLists slice of the store
 * @name loadListsForProject
 * @function
 * @param {issueId} - id - id for the issue
*/
export const loadListsForProject = id =>
    async dispatch => {
        const response = await dispatch(
                djangioActionCreators.resourceGetList(
                    DjangIO.app.userdata.models.NewList,
                    getIssueManagementLists,
                    { for_project_and_subprojects: id, limit: 500 },
                    { issueId: id }
                )
            )
        return dispatch(loadLists(response))
    }

/**
 * creates a new list, load it to the newLists slice of the store
 * @name createNewList
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {string} - name - what the newList is going to be named
 * @param {integer} - dataType - qdt for the list
 * @param {integer} - issueId - id for the issue
 * @param {obj} - searchifyFilters - obj that comes out of searchifySubmitFunc with added_ids, deleted_ids and filters
*/
export const createNewList = ({ name, dataType, issueId, searchifyFilters }) =>
    async (dispatch) => {
        const newList = {
            name,
            data_type: dataType,
            user: Userdata.id,
            project: issueId,
        }
        if (searchifyFilters) {
            Object.assign(newList, {
                added_ids: searchifyFilters.addedIds,
                deleted_ids: searchifyFilters.deletedIds,
                filters: searchifyFilters.querysetList,
            })
        }
        try {
            const response = await dispatch(
                djangioActionCreators.sendAction(
                    DjangIO.app.userdata.models.NewList,
                    submitNewList,
                    {
                        action: 'create_list',
                        kwargs: newList,
                        method: 'post',
                        payload: { issueId }
                    }
                )
            )
            await dispatch(loadList(response))
            return response
        } catch (error) {
            BACKENDERROR("Unable to Create List")
        }
    }

/**
 * Opens up powersearch with the filters required to give the contents of the list as search results
 * @name changeNewList
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {number} - id - the list id
 * @param {obj} - searchifyFilters - the filters used to get all the items for a list in the form 
 *      {
 *          addedIds: [...],
 *          deletedIds: [...],
 *          querysetList: [...],
 *      }
 */
 export const changeNewList = ({ id, searchifyFilters }) =>
   async (dispatch) => {
        try {
            const response = await dispatch( 
                djangioActionCreators.resourcePatchAction(
                    DjangIO.app.userdata.models.NewList,
                    editNewList,
                    id,
                    {
                        actionName: "edit_list",
                        searchifyFilters,
                    },
                    {
                        id,
                    }
                )
            )
            return response
        } catch (error) {
            BACKENDERROR("Unable to Change List")
        }
    }


/**
 * used to update the newList form values
 * @name editNewListForm
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {string} - field - which field of newListForm to edit (name, dataType)
 * @param {any} - value - value for the given field
 * @param {issueId} - id - id for the issue
*/
export const editNewListForm = ({ field, value, issueId }) => ({
    field,
    value,
    issueId,
    type: actionTypes.editNewListForm
})

/**
 * @name openCloseNewListForm
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {bool} - value - whether newList form should open or close
 * @param {issueId} - id - id for the issue
*/
export const openCloseNewListForm = ({ value, issueId }) => ({ value, issueId, type: actionTypes.openCloseNewListForm })


/////// To be passed to newList /////////
/**
 * creates an exact copy of the list, and appends to lists being show
 * @name cloneList
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {number} - issueId - id for the issue
 * @param {number} - id - id for the list being cloned
*/
export const cloneList = ({ id, issueId }) =>
    async dispatch => {
        const response = await dispatch(
            djangioActionCreators.sendAction(
                DjangIO.app.userdata.models.NewList.objects.get(id),
                submitNewList,
                {
                    action: 'clone',
                    method: 'post',
                    payload: { issueId }
                }
            )
        )
        return dispatch(loadList(response))
    }

/**
 * deletes a list and removes it from the lists in the page
 * @name deleteList
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {number} - issueId - id for the issue
 * @param {number} - id - id for the list
*/
export const deleteList = ({ id, issueId }) =>
    async dispatch => {
        const shouldDelete = await swal(swalContents.deleteListConfirm)
        if (!shouldDelete) {
            return;
        }
        return dispatch(djangioActionCreators.resourcePatchDetail(
            DjangIO.app.userdata.models.NewList,
            deleteListAC,
            id,
            { archived: true },
            { issueId, listId: id }
        ))
    }
//////////////////////////////////

/**
 * @name updateFilter
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {any} - value - the value (or values)
 * @param {number} - issueId - id for the issue
 * @param {string} - attr - the attribute of the list that the value should filter on,
 *                  eg name (should be defined by filterFunctions on lists-page-selectors )
*/
export const updateFilter = ({ value, issueId, attr }) =>
    ({ value, attr, issueId, type: actionTypes.updateFilter })


/**
 * @name updateSort
 * @function
 * All params are named params being passed via an obj as the first argument
 * @param {string} - value - the attribute on the lists to sort by, can have '-' prefix to reverse that sort
 * @param {number} - issueId - id for the issue
*/
export const updateSort = ({ value, issueId }) =>
    ({ value, issueId, type: actionTypes.updateSort })
